import React from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ControllerRenderProps } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";

export interface FormSelectOption {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface FormSelectProps {
  // react-hook-form mode
  field?: ControllerRenderProps<Record<string, unknown>, string>;
  // standalone mode
  value?: string | number;
  onChange?: (value: string | number) => void;
  // common
  options: FormSelectOption[];
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  // ── Additive (SETTINGS_V2_SP4 §4 — co-owned shared lib, logged in
  //    BREAKING_CHANGES.md). Both default OFF, so every existing caller is
  //    byte-identical.
  /** Visual mode: "dt" = existing dt-token styling (default); "cssVars" =
   *  defer every color/font to the ambient scoped CSS variables (settings-v2 /
   *  future v2). Mirrors FormTextField's `visual` (SP2). */
  visual?: "dt" | "cssVars";
  /** ClassName applied to the portalled menu Paper so it can carry the v2
   *  scope classes — MUI portals the menu to document.body, OUTSIDE the scoped
   *  root, so the CSS vars would otherwise not resolve in the OPEN dropdown
   *  (the same trap SP3 solved for the Radix dropdown portal). */
  dropdownClassName?: string;
  /** Disabled state (SETTINGS_V2_SP7 §3 — co-owned shared lib, logged in
   *  BREAKING_CHANGES.md). Default false, so every existing caller is
   *  byte-identical. In `visual="cssVars"` mode the disabled affordance mirrors
   *  FormTextField's cssVars precedent (var(--card-2) bg, muted text,
   *  not-allowed cursor). */
  disabled?: boolean;
}

// cssVars mode — defers every color/font to the ambient scoped CSS custom
// properties (they resolve because the control renders inside
// `.settings-v2-root`, and the portalled menu carries the scope classes via
// `dropdownClassName`). Mirrors FormTextField's cssVarsTextFieldSx (SP2).
const cssVarsSelectSx = {
  fontFamily: "inherit",
  fontSize: "13.5px",
  borderRadius: "8px",
  backgroundColor: "var(--bg-soft)",
  color: "var(--ink)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--mod-accent)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--mod-accent)",
    borderWidth: "1px",
  },
  "&.Mui-focused": { boxShadow: "0 0 0 3px var(--mod-accent-soft)" },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--red)" },
  "&.Mui-disabled": { backgroundColor: "var(--card-2)", cursor: "not-allowed" },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" },
  "& .MuiSelect-select.Mui-disabled": { WebkitTextFillColor: "var(--muted)", cursor: "not-allowed" },
};

const cssVarsPaperSx = {
  borderRadius: "10px",
  background: "var(--card)",
  border: "1px solid var(--border)",
  boxShadow: "0 12px 32px rgba(15, 23, 30, 0.16)",
  color: "var(--ink-2)",
  "& .MuiMenuItem-root": {
    fontFamily: "inherit",
    fontSize: "13.5px",
    color: "var(--ink-2)",
  },
  "& .MuiMenuItem-root:hover": { background: "var(--card-2)", color: "var(--ink)" },
  "& .MuiMenuItem-root.Mui-selected": { background: "var(--mod-accent-soft)" },
  "& .MuiMenuItem-root.Mui-selected:hover": { background: "var(--mod-accent-soft)" },
};

const dtSelectSx = {
  fontFamily: dt.font,
  fontSize: "14px",
  borderRadius: dt.radiusSm,
  backgroundColor: dt.inputBg,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: dt.cardBorder },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: dt.blue },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: dt.blue },
};

const dtPaperSx = {
  borderRadius: dt.radiusSm,
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
};

/**
 * Styled MUI Select dropdown with dt tokens (default) or scoped CSS vars.
 *
 * Based on SR urgency Select pattern with styled MenuProps paper.
 * Supports both react-hook-form Controller and standalone mode.
 */
const FormSelect: React.FC<FormSelectProps> = ({
  field,
  value,
  onChange,
  options,
  placeholder,
  error,
  helperText,
  fullWidth = true,
  size = "small",
  visual = "dt",
  dropdownClassName,
  disabled = false,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // Visual mode selects the base sx blocks + inline font/color values. "dt"
  // keeps every existing caller pixel-identical; "cssVars" defers to the
  // ambient scoped tokens.
  const isCss = visual === "cssVars";
  const menuFont = isCss ? "inherit" : dt.font;
  const placeholderColor = isCss ? "var(--faint)" : dt.textMuted;

  const resolvedValue = field ? (field.value as string | number) : (value ?? "");

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const newValue = event.target.value;
    if (field) {
      field.onChange(newValue === "" ? undefined : newValue);
    } else {
      onChange?.(newValue);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} error={error}>
      <Select
        value={resolvedValue}
        onChange={handleChange}
        disabled={disabled}
        displayEmpty={!!placeholder}
        sx={isCss ? cssVarsSelectSx : dtSelectSx}
        MenuProps={{
          PaperProps: {
            className: dropdownClassName,
            sx: isCss ? cssVarsPaperSx : dtPaperSx,
          },
        }}
        renderValue={(selected) => {
          if (!selected && selected !== 0) {
            return (
              <Box sx={{ color: placeholderColor, fontFamily: menuFont }}>
                {placeholder}
              </Box>
            );
          }
          const found = options.find((o) => o.value === selected);
          if (!found) return String(selected);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: menuFont,
              }}
            >
              {found.icon}
              {found.label}
            </Box>
          );
        }}
      >
        {placeholder && (
          <MenuItem value="" sx={{ fontFamily: menuFont, color: placeholderColor }}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={String(option.value)}
            value={option.value}
            sx={{ fontFamily: menuFont }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {option.icon}
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontFamily: menuFont }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormSelect;
